import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiSend, FiFileText, FiClock, FiAlertCircle, FiCheck, FiCalendar, FiCheckCircle, FiMessageSquare, FiSave, FiAward, FiPaperclip, FiChevronDown, FiChevronUp, FiEye, FiWifiOff, FiRefreshCw } from 'react-icons/fi';
import { utcToChicago, formatForDisplay, getCurrentWeekNumber, isWeekAvailableForSubmission, getWeekEndDate, isSubmissionLate } from '../../utils/datetime-comprehensive';
import { isOverdue, isDueSoon } from '../../utils/datetime-comprehensive';
import { toPlainTextFromHtml } from '../../lib/richtext/tiptapConverters';
import { sanitizeHtmlForViewer } from '../../lib/richtext/htmlSanitizer';
import apiService from '../../services/api';
import RichTextEditorTipTap from '../common/RichTextEditor';
import PdfViewer from '../common/PdfViewer';
import logger from '../../utils/logger';
import styles from './AssignmentSubmissionInline.module.css';

// ============================================
// PAGE CONTAINER - Centered with max-width
// ============================================
const PageContainer = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 2rem 1.5rem 6rem 1.5rem;
  background: var(--background-secondary);
  min-height: 100vh;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem 6rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem 6rem 0.75rem;
  }
`;

const Container = styled.div`
  width: 100%;
  min-height: auto;
  margin: 0;
  padding: 0;
  background: transparent;
  border-radius: var(--border-radius-lg);
  box-shadow: none;
  border: none;
  position: relative;
`;

// ============================================
// HEADER SECTION (Existing - Keep as is)
// ============================================
// ============================================
// MODERN HERO HEADER SECTION
// ============================================
const HeroHeader = styled.div`
  background: var(--background-primary);
  border-bottom: 1px solid var(--border-color);
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 20px 16px;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 10px;
  }
`;

const HeroBackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  cursor: pointer;
  padding: 0.625rem 1rem;
  border-radius: var(--border-radius);
  transition: all 0.2s ease;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  min-height: 40px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: var(--background-secondary);
      border-color: var(--primary-color);
      color: var(--primary-color);
    }
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    min-height: 36px;
  }
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid;
  white-space: nowrap;
  
  ${props => {
    if (props.$status === 'overdue') {
      return `
        background: rgba(239, 68, 68, 0.1);
        border-color: rgba(239, 68, 68, 0.2);
        color: var(--error-color);
      `;
    }
    if (props.$status === 'duesoon') {
      return `
        background: rgba(96, 165, 250, 0.1);
        border-color: rgba(96, 165, 250, 0.2);
        color: var(--info-color);
      `;
    }
    if (props.$status === 'submitted') {
      return `
        background: rgba(34, 197, 94, 0.1);
        border-color: rgba(34, 197, 94, 0.2);
        color: var(--success-color);
      `;
    }
    return '';
  }}
  
  svg {
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
    padding: 0.25rem 0.625rem;
  }
`;

const HeroTitle = styled.h1`
  color: var(--text-primary);
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  margin: 0 0 12px 0;
  font-family: 'Inter', 'Noto Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  line-height: 1.2;
  letter-spacing: -0.025em;
  word-wrap: break-word;
  overflow-wrap: break-word;

  * {
    font-size: inherit !important;
    font-weight: inherit !important;
    line-height: inherit !important;
    letter-spacing: inherit !important;
    font-family: inherit !important;
    color: inherit !important;
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const HeroSubtitle = styled.div`
  color: var(--text-muted);
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.5;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const SubtitleSeparator = styled.span`
  color: var(--text-muted);
  opacity: 0.5;
`;

// ============================================
// MOBILE: META CHIPS ROW
// ============================================
const MetaChipsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  
  @media (min-width: 1024px) {
    display: none;
  }
`;

const MetaChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  border-radius: 9999px;
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  
  svg {
    color: var(--primary-color);
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    padding: 0.375rem 0.625rem;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

// ============================================
// DESKTOP: SUMMARY BAR
// ============================================
const SummaryBar = styled.div`
  display: none;
  
  @media (min-width: 1024px) {
    display: block; /* Changed from grid to block - inner div handles grid layout */
    padding: 20px 24px;
    background: var(--background-primary);
    border: 1px solid var(--border-color);
    border-radius: var(--border-radius-lg);
    box-shadow: var(--shadow-sm);
    margin-top: 20px;
  }
`;

// ============================================
// TASK INSTRUCTIONS CARD
// ============================================
const TaskCard = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  margin-top: 20px;
`;

const TaskCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--background-primary);
  
  @media (max-width: 768px) {
    padding: 14px 16px;
  }
`;

const TaskCardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  svg {
    color: var(--primary-color);
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
`;

const TaskCardBody = styled.div`
  padding: 20px;
  color: var(--text-primary);
  font-size: var(--font-size-base, 0.9375rem);
  line-height: 1.65;
  font-family: 'Noto Sans Arabic', 'Amiri', 'Scheherazade New', 'Inter', 'Arial Unicode MS', 'Tahoma', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    padding: 16px;
  }

  /* Ensure all HTML elements use Arabic fonts first */
  * {
    font-family: 'Noto Sans Arabic', 'Amiri', 'Scheherazade New', 'Inter', 'Arial Unicode MS', 'Tahoma', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
  }
`;

const PdfAttachmentChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0.5rem 0.75rem;
  border-radius: 9999px;
  background: rgba(216, 184, 137, 0.1);
  border: 1px solid rgba(216, 184, 137, 0.2);
  color: var(--primary-color);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: rgba(216, 184, 137, 0.15);
      border-color: var(--primary-color);
    }
  }
  
  svg {
    flex-shrink: 0;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    padding: 0.375rem 0.625rem;
  }
`;

// ============================================
// OVERDUE WARNING (Keep for use below task card)
// ============================================
const OverdueWarning = styled.div`
  background: rgba(254, 243, 199, 0.4);
  border: 1px solid rgba(245, 158, 11, 0.3);
  color: var(--warning-dark);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 400;
  font-size: 0.875rem;

  svg {
    color: var(--warning-dark);
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    font-size: 0.8125rem;
    margin-top: 1.25rem;
    margin-bottom: 1.25rem;
  }
`;

// ============================================
// SUBMISSION SECTION - New Card Design
// ============================================
const SubmissionSection = styled.div`
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 768px) {
    margin-top: 20px;
    gap: 20px;
  }
`;

const SubmissionCard = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-md);
  overflow: visible;
`;

const CardBody = styled.div`
  padding: 24px;

  @media (max-width: 768px) {
    padding: 20px;
  }

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 18px;
  }

  @media (max-width: 480px) {
    margin-bottom: 16px;
  }

  /* RichTextEditor container responsiveness */
  > div {
    @media (max-width: 768px) {
      font-size: 0.875rem;
    }

    @media (max-width: 480px) {
      font-size: 0.8125rem;
    }
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 12px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

// FieldHelpText removed - no longer used

const CharacterCount = styled.div`
  text-align: right;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 8px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`;

const AutoSaveIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 12px;
  padding: 8px 12px;
  border-radius: var(--border-radius);
  background: rgba(216, 184, 137, 0.05);
  border: 1px solid rgba(216, 184, 137, 0.1);
  transition: all 0.2s ease;

  &.saving {
    color: var(--primary-color);
    background: rgba(216, 184, 137, 0.08);
    border-color: rgba(216, 184, 137, 0.2);
  }

  &.saved {
    color: var(--success-color);
    background: rgba(34, 197, 94, 0.05);
    border-color: rgba(34, 197, 94, 0.1);
  }

  &.error {
    color: var(--error-color);
    background: rgba(239, 68, 68, 0.05);
    border-color: rgba(239, 68, 68, 0.1);
  }

  &.offline {
    color: var(--warning-dark);
    background: rgba(245, 158, 11, 0.05);
    border-color: rgba(245, 158, 11, 0.1);
  }

  &.unsaved {
    color: var(--text-secondary);
    background: rgba(0, 0, 0, 0.03);
    border-color: var(--border-color);
  }
`;

const SmallSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(176, 141, 87, 0.3);
  border-top: 2px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  text-decoration: underline;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const FileInput = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  overflow: hidden;
  pointer-events: none;
`;

const PdfUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const PdfUploadButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  height: 40px;
  border: 1px solid var(--border-color, #e6e1d9);
  border-radius: 12px;
  background: transparent;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  touch-action: manipulation;
  
  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }
  
  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.03);
      border-color: var(--primary-color);
      color: var(--primary-color);
    }
  }
  
  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

const PdfSelectedText = styled.span`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  color: var(--text-primary);
  flex: 1;
  min-width: 0;
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const PdfRemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  white-space: nowrap;
  touch-action: manipulation;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--error-color);
      background: rgba(239, 68, 68, 0.05);
    }
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const PdfErrorText = styled.div`
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 12px;
  color: var(--error-color);
  margin-top: 8px;
`;


const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid rgba(216, 184, 137, 0.1);
  background: var(--background-secondary);

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    padding: 16px 20px;
    gap: 10px;
    
    button {
      width: 100%;
    }
  }

  @media (max-width: 480px) {
    padding: 14px 16px;
    gap: 8px;
  }
`;

const Button = styled.button`
  padding: 0.875rem 1.5rem;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9375rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  border: 1px solid transparent;
  white-space: nowrap;
  min-height: 48px;
  touch-action: manipulation;

  &.primary {
    background: var(--primary-color);
    color: var(--text-inverse);
    border-color: var(--primary-color);
    
    &:hover:not(:disabled) {
      background: var(--primary-hover);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(184, 149, 106, 0.3);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }

  &.secondary {
    background: var(--surface-color);
    color: var(--text-primary);
    border-color: var(--border-color);
    
    &:hover:not(:disabled) {
      border-color: var(--primary-color);
      background: var(--hover-overlay);
      color: var(--primary-color);
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.25rem;
    font-size: 0.875rem;
    min-height: 48px;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    min-height: 44px;
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// ============================================
// FEEDBACK CARD
// ============================================
const FeedbackCard = styled.div`
  background: var(--background-primary);
  border-radius: var(--border-radius-lg);
  border: 1px solid rgba(216, 184, 137, 0.1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const FeedbackHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(216, 184, 137, 0.1);
  background: var(--background-secondary);
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1.125rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    padding: 16px 20px;
    font-size: 1rem;
  }
`;

const FeedbackContent = styled.div`
  padding: 24px;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: var(--text-primary);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const GradeDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 16px;
  background: rgba(216, 184, 137, 0.05);
  border-radius: var(--border-radius);
  border: 1px solid rgba(216, 184, 137, 0.1);
`;

const GradeValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
`;

const GradeMax = styled.div`
  font-size: 1rem;
  color: var(--text-muted);
`;

const FeedbackText = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: var(--background-primary);
  border-radius: var(--border-radius);
  border: 1px solid rgba(216, 184, 137, 0.1);
  white-space: pre-wrap;
`;

const FeedbackTimestamp = styled.div`
  margin-top: 12px;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

// ============================================
// MESSAGES
// ============================================
const ErrorMessage = styled(motion.div)`
  background: rgba(239, 68, 68, 0.1);
    color: var(--error-color);
  padding: 12px 16px;
  border-radius: var(--border-radius);
  font-size: 0.875rem;
  border: 1px solid rgba(239, 68, 68, 0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 16px;
`;

const SuccessMessage = styled(motion.div)`
  background: rgba(34, 197, 94, 0.1);
  color: var(--success-color);
  padding: 12px 16px;
  border-radius: var(--border-radius);
  font-size: 0.875rem;
  border: 1px solid rgba(34, 197, 94, 0.2);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 16px;
`;

const WeeklySubmissionsSection = styled.div`
  background: var(--background-primary);
  border-radius: var(--border-radius);
  padding: 1.5rem;
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.875rem;
  }
`;

const WeeklySubmissionsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    margin-bottom: 0.75rem;
  }
`;

const WeeklySubmissionsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 0.9375rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.875rem;
    gap: 0.375rem;
    
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const WeeklySubmissionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
  
  @media (max-width: 480px) {
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }
`;

const WeeklySubmissionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.875rem;
  background: var(--background-secondary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  cursor: pointer;
  transition: all 0.2s ease;
  
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      background: var(--background-tertiary);
      border-color: var(--primary-color);
    }
  }
  
  ${props => props.$selected && `
    background: rgba(216, 184, 137, 0.1);
    border-color: var(--primary-color);
  `}
  
  @media (max-width: 480px) {
    padding: 0.75rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`;

const WeeklySubmissionInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const WeeklySubmissionWeek = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.9375rem;
  
  @media (max-width: 480px) {
    font-size: 0.875rem;
  }
`;

const WeeklySubmissionStatus = styled.div`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    gap: 0.25rem;
    
    svg {
      width: 12px;
      height: 12px;
    }
  }
`;

const WeekSelectorSection = styled.div`
  display: flex;
  gap: 0.875rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.75rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.625rem;
    margin-bottom: 0.875rem;
  }
`;

const WeekSelectorLabel = styled.label`
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 480px) {
    font-size: 0.8125rem;
  }
`;

const WeekSelect = styled.select`
  padding: 0.75rem 3rem 0.75rem 0.875rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 13px;
  line-height: 1.5;
  height: 40px;
  cursor: pointer;
  width: auto;
  max-width: 200px;
  appearance: none;
  -webkit-appearance: none; 
  -moz-appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: 1.25em 1.25em;
  box-sizing: border-box;
  vertical-align: middle;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    flex: 1;
    min-width: 140px;
    padding: 0.875rem 3rem 0.875rem 1rem;
    font-size: 14px;
    height: 46px;
    line-height: 1.5;
  }
  
  @media (max-width: 480px) {
    padding: 0.75rem 3rem 0.75rem 0.875rem;
    font-size: 13px;
    height: 42px;
    line-height: 1.5;
  }
`;


// ============================================
// MAX SUBMISSIONS MESSAGE
// ============================================
const MaxSubmissionsMessage = styled.div`
  padding: 0.75rem;
  background: rgba(216, 184, 137, 0.1);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: 0.875rem;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    padding: 0.625rem;
    font-size: 0.8125rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

const CollapsibleMetaRow = styled.div`
  cursor: pointer;
  user-select: none;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const CollapsibleContent = styled.div`
  margin-top: 0.5rem;
  padding-left: calc(var(--meta-icon-size) + var(--meta-icon-gap) + var(--meta-label-width));
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const WeekSubmissionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: var(--background-secondary);
  border-radius: var(--border-radius);
  border: 1px solid var(--border-color);
  font-size: 0.8125rem;
  color: var(--text-secondary);
  
  .week-label {
    font-weight: 600;
    color: var(--text-primary);
    min-width: 60px;
  }
`;

// ============================================
// MAIN COMPONENT
// ============================================
function AssignmentSubmissionInline({ assignment, existingSubmission, onSubmissionSuccess, onBack, preselectedWeek }) {
  const [content, setContent] = useState({ 
    content_json: { type: 'doc', content: [] }, 
    content_html: '<p></p>', 
    content_plain: '' 
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPdfData, setCurrentPdfData] = useState(null);
  const [showInstructorPdf, setShowInstructorPdf] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pdfError, setPdfError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Enhanced auto-save state
  const [isDirty, setIsDirty] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error' | 'offline'
  const [autoSaveError, setAutoSaveError] = useState(null);

  // Refs for debouncing and tracking
  const debounceTimerRef = useRef(null);
  const lastSavedContentRef = useRef(null);
  const retryCountRef = useRef(0);
  const maxRetries = 3;
  const isSubmittingRef = useRef(false); // Prevent auto-save during submit
  
  // Weekly submissions state
  const [weeklySubmissions, setWeeklySubmissions] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [loadingWeeklySubmissions, setLoadingWeeklySubmissions] = useState(false);
  const [currentSubmission, setCurrentSubmission] = useState(existingSubmission);
  const [submissionsExpanded, setSubmissionsExpanded] = useState(false);
  const [dueDateExpanded, setDueDateExpanded] = useState(false);
  
  const isWeeklySubmission = assignment?.allow_weekly_submissions === 1 || assignment?.allow_weekly_submissions === true;
  const maxWeeklySubmissions = assignment?.max_weekly_submissions || 0;
  
  // Calculate available weeks based on start_date
  const getAvailableWeeks = () => {
    if (!isWeeklySubmission || !assignment?.start_date) {
      // If no start_date, allow all weeks up to max (legacy behavior)
      return Array.from({ length: maxWeeklySubmissions }, (_, i) => i + 1);
    }
    
    const currentWeek = getCurrentWeekNumber(assignment.start_date);
    if (currentWeek === 0) {
      // Start date hasn't passed yet
      return [];
    }
    
    // Return weeks from 1 to currentWeek (can submit for current or past weeks)
    return Array.from({ length: Math.min(currentWeek, maxWeeklySubmissions) }, (_, i) => i + 1);
  };
  
  const availableWeeks = getAvailableWeeks();
  
  // Treat as submitted only if submitted_at looks like a real timestamp
  const submittedAt = currentSubmission?.submitted_at;
  const hasRealSubmission = !!(
    submittedAt &&
    submittedAt !== '' &&
    submittedAt !== '0000-00-00 00:00:00' &&
    submittedAt !== '1970-01-01T00:00:00.000Z' &&
    submittedAt !== '0000-00-00T00:00:00.000Z' &&
    submittedAt !== null &&
    submittedAt !== undefined
  );
  const isReadOnly = !!(currentSubmission && hasRealSubmission && currentSubmission.resubmission_state !== 'open_for_resubmission');

  // Fetch all weekly submissions if assignment allows weekly submissions
  useEffect(() => {
    if (assignment && isWeeklySubmission) {
      const fetchWeeklySubmissions = async () => {
        try {
          setLoadingWeeklySubmissions(true);
          const response = await apiService.getMyAssignmentSubmissions(assignment.id);
          if (response.success && response.submissions) {
            setWeeklySubmissions(response.submissions);

            // If we have a preselectedWeek, check if existingSubmission matches it
            if (preselectedWeek) {
              const weekNum = Number(preselectedWeek);
              // If existingSubmission matches the preselectedWeek, use it directly
              if (existingSubmission && existingSubmission.week_number === weekNum) {
                setCurrentSubmission(existingSubmission);
                setSelectedWeek(weekNum);
              }
              // Otherwise, let the preselection effect find it from the list
            } else if (existingSubmission) {
              // No preselection, use existingSubmission as before
              const found = response.submissions.find(s => s.id === existingSubmission.id);
              if (found) {
                setCurrentSubmission(found);
                setSelectedWeek(found.week_number);
              }
            }
          }
        } catch (error) {
          logger.error('Error fetching weekly submissions', { error, assignmentId: assignment.id });
        } finally {
          setLoadingWeeklySubmissions(false);
        }
      };
      fetchWeeklySubmissions();
    } else {
      setCurrentSubmission(existingSubmission);
    }
  }, [assignment, isWeeklySubmission, existingSubmission, preselectedWeek]);

  // Handle pre-selected week from URL parameters
  useEffect(() => {
    if (preselectedWeek && isWeeklySubmission && !selectedWeek) {
      const weekNum = Number(preselectedWeek);
      // Always honor the preselected week from URL, even if it's not in availableWeeks
      setSelectedWeek(weekNum);
      // If this week has an existing submission, load it; otherwise clear currentSubmission
      const existingSubmissionForWeek = weeklySubmissions.find(s => s.week_number === weekNum);
      if (existingSubmissionForWeek) {
        setCurrentSubmission(existingSubmissionForWeek);
      } else {
        // No submission for this week yet, clear it
        setCurrentSubmission(null);
      }
    }
  }, [preselectedWeek, isWeeklySubmission, selectedWeek, weeklySubmissions]);

  // Handle existing submission
  useEffect(() => {
    if (currentSubmission) {
      // Handle both string and object content formats
      if (typeof currentSubmission.content === 'string' && currentSubmission.content.trim()) {
        setContent({ 
          content_json: null, 
          content_html: currentSubmission.content, 
          content_plain: toPlainTextFromHtml(currentSubmission.content) 
        });
      } else if (currentSubmission.content && typeof currentSubmission.content === 'object' && 
                 (currentSubmission.content.html || currentSubmission.content.content_html)) {
        logger.debug('Setting content as object', { content: currentSubmission.content });
        setContent(currentSubmission.content);
      } else {
        logger.debug('Setting default empty content - content is null/empty');
        setContent({ 
          content_json: { type: 'doc', content: [] }, 
          content_html: '<p></p>', 
          content_plain: '' 
        });
      }
      
      // Load existing PDF if it exists
      if (currentSubmission.pdf_attachment) {
        const existingPdfFile = new File([], 'existing_submission.pdf', { type: 'application/pdf' });
        setPdfFile(existingPdfFile);
        setPdfFileName('existing_submission.pdf');
      } else {
        setPdfFile(null);
        setPdfFileName('');
      }
      
      // Check if assignment is actually submitted
      if (currentSubmission.submitted_at !== null && currentSubmission.submitted_at !== undefined) {
        if (currentSubmission.resubmission_state === 'open_for_resubmission') {
          setSuccess(`Week ${currentSubmission.week_number || ''}: This is your submitted response. You can edit and resubmit it.`.trim());
        } else {
          setSuccess(`Week ${currentSubmission.week_number || ''}: This is your submitted response. You cannot edit it.`.trim());
        }
      } else {
        setSuccess('');
      }
    } else {
      setContent({ raw: null, html: '<p></p>', text: '' });
      setSuccess('');
    }
    
    setError('');
  }, [currentSubmission, assignment]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty && !isReadOnly) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty, isReadOnly]);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => {
      setAutoSaveStatus(prev => prev === 'offline' ? 'idle' : prev);
    };
    const handleOffline = () => {
      setAutoSaveStatus('offline');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Auto-save function with retry logic
  const performAutoSave = useCallback(async (contentToSave, pdfToSave) => {
    // Don't auto-save if we're in the middle of submitting
    if (isSubmittingRef.current) {
      return;
    }

    // Don't auto-save if offline
    if (!navigator.onLine) {
      setAutoSaveStatus('offline');
      return;
    }

    // Don't auto-save if no real content
    const hasActualContent = contentToSave.replace(/<[^>]*>/g, '').trim().length > 0;
    if (!hasActualContent && !pdfToSave) {
      return;
    }

    // Check if content actually changed from last save
    const contentHash = JSON.stringify({ content: contentToSave, hasPdf: !!pdfToSave });
    if (lastSavedContentRef.current === contentHash) {
      // Content hasn't changed, no need to save
      setIsDirty(false);
      return;
    }

    try {
      setAutoSaveStatus('saving');
      setSaving(true);

      let pdfBase64 = null;
      if (pdfToSave) {
        if (pdfToSave.name !== 'existing_submission.pdf') {
          pdfBase64 = await convertFileToBase64(pdfToSave);
        } else {
          pdfBase64 = currentSubmission?.pdf_attachment || null;
        }
      }

      await apiService.saveAssignmentDraft(assignment.id, contentToSave, pdfBase64, selectedWeek);

      // Success - reset retry count and update state
      retryCountRef.current = 0;
      lastSavedContentRef.current = contentHash;
      setLastSaved(new Date());
      setAutoSaveStatus('saved');
      setAutoSaveError(null);
      setIsDirty(false);

      // Reset to idle after 3 seconds
      setTimeout(() => {
        setAutoSaveStatus(prev => prev === 'saved' ? 'idle' : prev);
      }, 3000);

    } catch (error) {
      logger.error('Auto-save failed', { error, assignmentId: assignment?.id, attempt: retryCountRef.current + 1 });

      // Retry logic with exponential backoff
      if (retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const backoffDelay = Math.pow(2, retryCountRef.current) * 1000; // 2s, 4s, 8s

        setAutoSaveStatus('error');
        setAutoSaveError(`Save failed. Retrying in ${backoffDelay / 1000}s...`);

        setTimeout(() => {
          performAutoSave(contentToSave, pdfToSave);
        }, backoffDelay);
      } else {
        // Max retries reached
        setAutoSaveStatus('error');
        setAutoSaveError('Auto-save failed. Click to retry.');
        retryCountRef.current = 0;
      }
    } finally {
      setSaving(false);
    }
  }, [assignment, selectedWeek, currentSubmission?.pdf_attachment]);

  // Debounced auto-save - triggers 3 seconds after user stops typing
  useEffect(() => {
    if (!assignment || isReadOnly) {
      return;
    }

    // Mark as dirty when content changes
    const contentToSave = (typeof content === 'object' && (content?.html || content?.content_html))
      ? (content.html || content.content_html).trim()
      : (typeof content === 'string' ? content.trim() : '');

    const contentHash = JSON.stringify({ content: contentToSave, hasPdf: !!pdfFile });
    if (lastSavedContentRef.current !== contentHash) {
      setIsDirty(true);
    }

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new debounce timer - 3 seconds after last change
    debounceTimerRef.current = setTimeout(() => {
      performAutoSave(contentToSave, pdfFile);
    }, 3000);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [content, pdfFile, assignment, isReadOnly, performAutoSave]);

  // Manual retry function for failed auto-saves
  const retryAutoSave = useCallback(() => {
    const contentToSave = (typeof content === 'object' && (content?.html || content?.content_html))
      ? (content.html || content.content_html).trim()
      : (typeof content === 'string' ? content.trim() : '');

    retryCountRef.current = 0;
    performAutoSave(contentToSave, pdfFile);
  }, [content, pdfFile, performAutoSave]);

  // Manual save functionality
  const saveDraft = async () => {
    if (isReadOnly) {
      setError('This assignment has already been submitted and cannot be modified.');
      return;
    }

    // Check if online
    if (!navigator.onLine) {
      setError('You are offline. Please check your internet connection and try again.');
      return;
    }

    const textContent = typeof content === 'object' ? (content.content_plain || content.text || '') : content;
    const hasText = textContent.replace(/<[^>]*>/g, '').trim().length > 0;
    const hasPdf = !!pdfFile;

    if (!assignment || (!hasText && !hasPdf)) {
      setError('Please enter some text or attach a PDF file before saving.');
      return;
    }

    // Cancel any pending auto-save
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    try {
      setSaving(true);
      setAutoSaveStatus('saving');
      setError('');

      let contentToSave;
      if (typeof content === 'object' && content !== null) {
        contentToSave = content.content_html || content.html || '';
      } else if (typeof content === 'string') {
        contentToSave = content;
      } else {
        contentToSave = '';
      }

      let pdfBase64 = null;
      if (pdfFile) {
        if (pdfFile.name !== 'existing_submission.pdf') {
          pdfBase64 = await convertFileToBase64(pdfFile);
        } else {
          pdfBase64 = currentSubmission?.pdf_attachment || null;
        }
      }

      await apiService.saveAssignmentDraft(assignment.id, contentToSave, pdfBase64, selectedWeek);

      // Update tracking state
      const contentHash = JSON.stringify({ content: contentToSave, hasPdf: !!pdfFile });
      lastSavedContentRef.current = contentHash;
      retryCountRef.current = 0;

      setLastSaved(new Date());
      setIsDirty(false);
      setAutoSaveStatus('saved');
      setAutoSaveError(null);
      setSuccess(selectedWeek ? `Week ${selectedWeek} draft saved successfully! You can continue editing or submit when ready.` : 'Draft saved successfully! You can continue editing or submit when ready.');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      logger.error('Save failed', { error, assignmentId: assignment?.id });
      setAutoSaveStatus('error');

      if (String(error?.message || '').toLowerCase().includes('already been submitted')) {
        setError(selectedWeek ? `Week ${selectedWeek} has already been submitted and cannot be modified.` : 'This assignment has already been submitted and cannot be modified.');
      } else if (String(error?.message || '').toLowerCase().includes('network') || error?.name === 'TypeError') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Failed to save draft. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePdfFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Clear previous PDF error
      setPdfError('');
      
      if (file.type !== 'application/pdf') {
        setPdfError('Only PDF files are supported');
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) {
        setPdfError('PDF file size must be less than 10MB');
        // Clear the file input
        e.target.value = '';
        return;
      }
      
      setPdfFile(file);
      setPdfFileName(file.name);
      setPdfError('');
    }
  };

  const removePdfFile = () => {
    setPdfFile(null);
    setPdfFileName('');
    setPdfError('');
    // Clear the file input
    const fileInput = document.getElementById('pdf-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };
  
  const truncateFileName = (fileName, maxLength = 30) => {
    if (!fileName) return '';
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.slice(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.slice(0, maxLength - extension.length - 4);
    return `${truncatedName}...${extension}`;
  };

  // handleViewPdf removed - no longer used
  // getCurrentPdfData removed - no longer used

  const getCurrentPdfFileName = () => {
    if (showInstructorPdf) {
      return `${assignment.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_attachment.pdf`;
    }
    if (currentSubmission?.pdf_attachment && !showInstructorPdf && currentPdfData) {
      // Submitted PDF attachment
      const studentName = currentSubmission.first_name && currentSubmission.last_name
        ? `${currentSubmission.first_name}_${currentSubmission.last_name}`.replace(/[^a-z0-9]/gi, '_').toLowerCase()
        : 'student';
      const assignmentTitle = assignment?.title?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'assignment';
      const weekSuffix = isWeeklySubmission && currentSubmission.week_number 
        ? `_week${currentSubmission.week_number}` 
        : '';
      return `${studentName}_${assignmentTitle}${weekSuffix}_submission.pdf`;
    }
    if (pdfFile && pdfFile.name !== 'existing_submission.pdf') {
      return pdfFile.name;
    }
    return pdfFileName || 'assignment.pdf';
  };

  const handleViewInstructorPdf = () => {
    if (assignment.pdf_attachment) {
      try {
        let cleanBase64 = assignment.pdf_attachment;
        
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        
        cleanBase64 = cleanBase64.replace(/\s/g, '');
        
        try {
          atob(cleanBase64);
          logger.debug('PDF base64 validation successful', { length: cleanBase64.length });
          
          setCurrentPdfData(cleanBase64);
          setShowInstructorPdf(true);
          setShowPdfViewer(true);
          setError('');
        } catch (decodeError) {
          logger.error('Base64 decode error', { error: decodeError, assignmentId: assignment?.id });
          setError('The PDF attachment data is not in a valid format. Please contact the instructor.');
        }
      } catch (error) {
        logger.error('Error processing PDF attachment', { error, assignmentId: assignment?.id });
        setError('Failed to load PDF attachment. Please try again or contact the instructor.');
      }
    } else {
      setError('No PDF attachment found for this assignment.');
    }
  };

  const handleViewSubmittedPdf = () => {
    if (currentSubmission?.pdf_attachment) {
      try {
        let cleanBase64 = currentSubmission.pdf_attachment;
        
        if (cleanBase64.includes(',')) {
          cleanBase64 = cleanBase64.split(',')[1];
        }
        
        cleanBase64 = cleanBase64.replace(/\s/g, '');
        
        try {
          atob(cleanBase64);
          logger.debug('Submitted PDF base64 validation successful', { length: cleanBase64.length });
          
          setCurrentPdfData(cleanBase64);
          setShowInstructorPdf(false);
          setShowPdfViewer(true);
          setError('');
        } catch (decodeError) {
          logger.error('Base64 decode error for submitted PDF', { error: decodeError, submissionId: currentSubmission?.id });
          setError('The PDF attachment data is not in a valid format.');
        }
      } catch (error) {
        logger.error('Error processing submitted PDF attachment', { error, submissionId: currentSubmission?.id });
        setError('Failed to load PDF attachment. Please try again.');
      }
    } else {
      setError('No PDF attachment found for this submission.');
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = error => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent auto-save during submit to avoid race conditions
    isSubmittingRef.current = true;

    // Cancel any pending auto-save
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Check if course is inactive
    // Note: Backend will handle override for specific users (e.g., shahab.sayeedi@gmail.com)
    // Frontend check is informational only - backend has final say
    if (assignment?.course_is_active === false) {
      // Don't block here - let backend handle the override logic
      // The error will be shown if backend rejects it
    }

    const textContent = typeof content === 'object' ? (content.content_plain || content.text || '') : content;
    if (!textContent.trim() && !pdfFile) {
      setError('Please enter your response to the assignment or attach a PDF file');
      isSubmittingRef.current = false;
      return;
    }

    setLoading(true);
    setError('');

    try {
      const contentToSubmit = (typeof content === 'object' && (content?.html || content?.content_html)) 
        ? (content.html || content.content_html).trim() 
        : (typeof content === 'string' ? content.trim() : '');
      
      let pdfBase64 = null;
      if (pdfFile) {
        if (pdfFile.name !== 'existing_submission.pdf') {
          pdfBase64 = await convertFileToBase64(pdfFile);
        } else {
          pdfBase64 = currentSubmission?.pdf_attachment || null;
        }
      }
      
      // Only include weekNumber for weekly submissions
      const submissionData = {
        content: contentToSubmit,
        pdf_attachment: pdfBase64
      };
      
      // Only add weekNumber if it's a weekly submission and selectedWeek is not null
      if (isWeeklySubmission && selectedWeek !== null && selectedWeek !== undefined) {
        submissionData.weekNumber = selectedWeek;
      }
      
      const response = await apiService.submitAssignment(assignment.id, submissionData);

      if (response.success) {
        // Update lastSavedContentRef to prevent auto-save from triggering after submit
        const contentHash = JSON.stringify({ content: contentToSubmit, hasPdf: !!pdfFile });
        lastSavedContentRef.current = contentHash;
        setIsDirty(false);
        setAutoSaveStatus('saved');

        setSuccess(selectedWeek ? `Week ${selectedWeek} submitted successfully!` : 'Assignment submitted successfully! Redirecting to assignments...');
        // Refresh weekly submissions if weekly submission
        if (isWeeklySubmission) {
          try {
            const submissionsResponse = await apiService.getMyAssignmentSubmissions(assignment.id);
            if (submissionsResponse.success && submissionsResponse.submissions) {
              setWeeklySubmissions(submissionsResponse.submissions);
              const submitted = submissionsResponse.submissions.find(s => s.week_number === selectedWeek);
              if (submitted) {
                setCurrentSubmission(submitted);
              }
            }
          } catch (error) {
            logger.error('Error refreshing weekly submissions', { error });
          }
        }
        if (onSubmissionSuccess) {
          onSubmissionSuccess(response.submission);
        }
        if (!isWeeklySubmission) {
          setTimeout(() => {
            if (onBack) {
              onBack();
            }
          }, 2000);
        }
      } else {
        setError(response.error || 'Failed to submit assignment');
      }
    } catch (error) {
      logger.error('Submit assignment error', { error, assignmentId: assignment?.id });
      setError('Failed to submit assignment. Please try again.');
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Only show overdue if assignment hasn't been submitted yet
  // Once submitted, check the submission status instead
  const assignmentOverdue = assignment && !hasRealSubmission && isOverdue(assignment.due_date);
  const assignmentDueSoon = assignment && !hasRealSubmission && isDueSoon(assignment.due_date);

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    try {
      const chicago = utcToChicago(dateString);
      return formatForDisplay(chicago, { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric', 
        hour: 'numeric', 
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      logger.error('Error formatting date', { error, dateString });
      return 'Invalid date';
    }
  };

  const formatDateWithSplit = (dateString, type = 'posted') => {
    const fullDate = formatDate(dateString);
    if (fullDate === 'No date' || fullDate === 'Invalid date') {
      // Special case for due date
      const displayText = type === 'due' ? 'No due date' : fullDate;
      return { datePart: displayText, timePart: null };
    }
    // Split "January 7, 2026 at 10:10:53 PM CST" into date and time parts
    const match = fullDate.match(/^(.*?)\s+at\s+(.*)$/);
    if (match) {
      return { datePart: match[1], timePart: match[2] };
    }
    return { datePart: fullDate, timePart: null };
  };

  // formatFileSize removed - no longer used

  if (!assignment) return null;

  const canEdit = !currentSubmission || currentSubmission.submitted_at === null || currentSubmission.resubmission_state === 'open_for_resubmission';
  
  // For weekly submissions, require week selection
  const canSubmit = !isWeeklySubmission || selectedWeek !== null;

  return (
    <PageContainer>
      <Container>
        {/* Modern Hero Header */}
        <HeroHeader>
          <TopRow>
            <HeroBackButton onClick={onBack}>
              <FiArrowLeft size={16} />
              Back to Assignments
            </HeroBackButton>
            {(assignmentOverdue || assignmentDueSoon || (hasRealSubmission && currentSubmission?.resubmission_state !== 'open_for_resubmission')) && (
              <StatusChip 
                $status={assignmentOverdue ? 'overdue' : (hasRealSubmission && currentSubmission?.resubmission_state !== 'open_for_resubmission') ? 'submitted' : assignmentDueSoon ? 'duesoon' : 'duesoon'}
              >
                {assignmentOverdue && (
                  <>
                    <FiAlertCircle size={12} />
                    Overdue
                  </>
                )}
                {!assignmentOverdue && hasRealSubmission && currentSubmission?.resubmission_state !== 'open_for_resubmission' && (
                  <>
                    <FiCheckCircle size={12} />
                    Submitted
                  </>
                )}
                {!assignmentOverdue && !hasRealSubmission && assignmentDueSoon && (
                  <>
                    <FiClock size={12} />
                    Due Soon
                  </>
                )}
              </StatusChip>
            )}
          </TopRow>
          
          <HeroTitle dangerouslySetInnerHTML={{ __html: (isWeeklySubmission && selectedWeek ? `Week ${selectedWeek}: ` : '') + sanitizeHtmlForViewer(assignment.title) }} />
          
          <HeroSubtitle>
            <span>{assignment.course_name}</span>
            {assignment.instructor_name && (
              <>
                <SubtitleSeparator>•</SubtitleSeparator>
                <span>{assignment.instructor_name}</span>
              </>
            )}
          </HeroSubtitle>
          
          {/* Mobile: Meta Chips */}
          <MetaChipsRow>
            <MetaChip>
              <FiCalendar />
              <span>Posted: <span style={{ marginLeft: '4px' }}>{formatDate(assignment.posted_date).split(',')[0]}</span></span>
            </MetaChip>
            {isWeeklySubmission && assignment?.start_date ? (
              <div style={{ width: '100%' }}>
                <MetaChip 
                  onClick={() => setDueDateExpanded(!dueDateExpanded)}
                  style={{ cursor: 'pointer', width: '100%', justifyContent: 'space-between' }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <FiClock />
                    <span>Due: <span style={{ marginLeft: '4px' }}>{dueDateExpanded ? 'Hide Due Date' : 'Show Due Date'}</span></span>
                  </span>
                  {dueDateExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                </MetaChip>
                {dueDateExpanded && (
                  <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {availableWeeks.map(weekNum => {
                      const weekEndDate = getWeekEndDate(assignment.start_date, weekNum);
                    if (weekEndDate && weekEndDate.isValid) {
                      const isoString = weekEndDate.toUTC().toISO();
                        return (
                          <MetaChip key={weekNum} style={{ paddingLeft: '1.5rem', fontSize: '0.75rem' }}>
                            <span>Week {weekNum}: <span style={{ marginLeft: '4px' }}>{formatDate(isoString)}</span></span>
                          </MetaChip>
                        );
                  }
                      return null;
                    })}
                  </div>
                )}
              </div>
            ) : (
              <MetaChip>
                <FiClock />
                <span>Due: <span style={{ marginLeft: '4px' }}>{formatDateWithSplit(assignment.due_date, 'due').datePart}</span></span>
            </MetaChip>
            )}
            <MetaChip>
              <FiAward />
              <span>Total: <span style={{ marginLeft: '4px' }}>{assignment.points} points</span></span>
            </MetaChip>
            {hasRealSubmission && currentSubmission?.submitted_at && (
              <>
                {isWeeklySubmission && weeklySubmissions.length > 0 ? (
                  <div style={{ width: '100%' }}>
                    <MetaChip 
                      onClick={() => setSubmissionsExpanded(!submissionsExpanded)}
                      style={{ cursor: 'pointer', width: '100%', justifyContent: 'space-between' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <FiCheckCircle />
                        <span>Submitted: <span style={{ marginLeft: '4px' }}>{submissionsExpanded ? 'Hide Submission Time' : 'Show Submission Time'}</span></span>
                      </span>
                      {submissionsExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </MetaChip>
                    {submissionsExpanded && (
                      <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {weeklySubmissions
                          .filter(sub => sub.submitted_at && sub.submitted_at !== '0000-00-00 00:00:00' && sub.submitted_at !== '1970-01-01T00:00:00.000Z')
                          .sort((a, b) => a.week_number - b.week_number)
                          .map(submission => (
                            <MetaChip key={submission.id} style={{ paddingLeft: '1.5rem', fontSize: '0.75rem' }}>
                              <span>Week {submission.week_number}: <span style={{ marginLeft: '4px' }}>{formatDate(submission.submitted_at).split(',')[0]}</span></span>
                            </MetaChip>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
              <MetaChip>
                <FiCheckCircle />
                <span>Submitted: <span style={{ marginLeft: '4px' }}>{formatDate(currentSubmission.submitted_at).split(',')[0]}</span></span>
              </MetaChip>
                )}
              </>
            )}
          </MetaChipsRow>
        </HeroHeader>

        {/* Desktop: Summary Bar */}
        <SummaryBar>
          <div className={styles.summaryBar}>
            <div className={styles.summaryLeft}>
              <div className={styles.metaRow}>
                <FiCalendar />
                <span className={styles.metaLabel}>Posted</span>
                <span className={styles.metaValue}>
                  <span className={styles.metaDate}>{formatDateWithSplit(assignment.posted_date).datePart}</span>
                  {formatDateWithSplit(assignment.posted_date).timePart && (
                    <span className={styles.metaTime}> at {formatDateWithSplit(assignment.posted_date).timePart}</span>
                  )}
                </span>
              </div>
              {isWeeklySubmission && assignment?.start_date ? (
                <div>
                  <CollapsibleMetaRow 
                    className={styles.metaRow}
                    onClick={() => setDueDateExpanded(!dueDateExpanded)}
                  >
                <FiClock />
                <span className={styles.metaLabel}>Due</span>
                    <span className={styles.metaValue} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                      <span>{dueDateExpanded ? 'Hide Due Date' : 'Show Due Date'}</span>
                      {dueDateExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                    </span>
                  </CollapsibleMetaRow>
                  {dueDateExpanded && (
                    <CollapsibleContent>
                      {availableWeeks.map(weekNum => {
                        const weekEndDate = getWeekEndDate(assignment.start_date, weekNum);
                        if (weekEndDate && weekEndDate.isValid) {
                          const isoString = weekEndDate.toUTC().toISO();
                          const parts = formatDateWithSplit(isoString);
                          return (
                            <WeekSubmissionItem key={weekNum}>
                              <span className="week-label">Week {weekNum}:</span>
                              <span>
                              <span className={styles.metaDate}>{parts.datePart}</span>
                              {parts.timePart && (
                                <span className={styles.metaTime}> at {parts.timePart}</span>
                              )}
                              </span>
                            </WeekSubmissionItem>
                          );
                      }
                        return null;
                      })}
                    </CollapsibleContent>
                  )}
                </div>
              ) : (
                <div className={styles.metaRow}>
                  <FiClock />
                  <span className={styles.metaLabel}>Due</span>
                  <span className={styles.metaValue}>
                        <span className={styles.metaDate}>{formatDateWithSplit(assignment.due_date).datePart}</span>
                        {formatDateWithSplit(assignment.due_date).timePart && (
                          <span className={styles.metaTime}> at {formatDateWithSplit(assignment.due_date).timePart}</span>
                        )}
                </span>
              </div>
              )}
            </div>
            <div className={styles.summaryRight}>
              <div className={styles.totalChip}>
                <FiAward />
                <span className={styles.metaLabel}>Total</span>
                <span className={styles.metaValue}>{assignment.points} points</span>
              </div>
              {hasRealSubmission && currentSubmission?.submitted_at && (
                <>
                  {isWeeklySubmission && weeklySubmissions.length > 0 ? (
                    <div>
                      <CollapsibleMetaRow 
                        className={styles.metaRow}
                        onClick={() => setSubmissionsExpanded(!submissionsExpanded)}
                      >
                        <FiCheckCircle />
                        <span className={styles.metaLabel}>Submitted</span>
                        <span className={styles.metaValue} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                          <span>{submissionsExpanded ? 'Hide Submission Time' : 'Show Submission Time'}</span>
                          {submissionsExpanded ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
                        </span>
                      </CollapsibleMetaRow>
                      {submissionsExpanded && (
                        <CollapsibleContent>
                          {weeklySubmissions
                            .filter(sub => sub.submitted_at && sub.submitted_at !== '0000-00-00 00:00:00' && sub.submitted_at !== '1970-01-01T00:00:00.000Z')
                            .sort((a, b) => a.week_number - b.week_number)
                            .map(submission => (
                              <WeekSubmissionItem key={submission.id}>
                                <span className="week-label">Week {submission.week_number}:</span>
                                <span>
                                  <span className={styles.metaDate}>{formatDateWithSplit(submission.submitted_at).datePart}</span>
                                  {formatDateWithSplit(submission.submitted_at).timePart && (
                                    <span className={styles.metaTime}> at {formatDateWithSplit(submission.submitted_at).timePart}</span>
                                  )}
                                </span>
                              </WeekSubmissionItem>
                            ))}
                        </CollapsibleContent>
                      )}
                    </div>
                  ) : (
                <div className={styles.metaRow}>
                  <FiCheckCircle />
                  <span className={styles.metaLabel}>Submitted</span>
                  <span className={styles.metaValue}>
                    <span className={styles.metaDate}>{formatDateWithSplit(currentSubmission.submitted_at).datePart}</span>
                    {formatDateWithSplit(currentSubmission.submitted_at).timePart && (
                      <span className={styles.metaTime}> at {formatDateWithSplit(currentSubmission.submitted_at).timePart}</span>
                    )}
                  </span>
                </div>
                  )}
                </>
              )}
            </div>
          </div>
        </SummaryBar>

        {/* Overdue Warning */}
        {assignmentOverdue && (
          <OverdueWarning>
            <FiAlertCircle size={16} />
            <strong>This assignment is overdue.</strong> Your submission will be marked as late.
          </OverdueWarning>
        )}

        {/* Task Instructions Card */}
        <TaskCard>
          <TaskCardHeader>
            <TaskCardTitle>
              <FiFileText />
              Task Instructions
            </TaskCardTitle>
          </TaskCardHeader>
          <TaskCardBody className={styles.taskCardBody}>
            <div className={styles.taskInstructionsBody} dangerouslySetInnerHTML={{ __html: sanitizeHtmlForViewer(assignment.description) }} />
            {assignment.pdf_attachment && (() => {
              // Calculate file size from base64
              let cleanBase64 = assignment.pdf_attachment;
              if (cleanBase64.includes(',')) {
                cleanBase64 = cleanBase64.split(',')[1];
              }
              cleanBase64 = cleanBase64.replace(/\s/g, '');
              const fileSizeKB = ((cleanBase64.length * 3) / 4 / 1024).toFixed(1);
              
              return (
                <div style={{ marginTop: '16px' }}>
                  <PdfAttachmentChip onClick={handleViewInstructorPdf}>
                    <FiPaperclip />
                    View Attached PDF ({fileSizeKB} KB)
                  </PdfAttachmentChip>
                </div>
              );
            })()}
          </TaskCardBody>
        </TaskCard>

        {/* Due Soon Warning - Only show if not already submitted */}
        {assignmentDueSoon && !assignmentOverdue && !hasRealSubmission && (
          <OverdueWarning style={{ background: 'rgba(96, 165, 250, 0.1)', borderColor: 'var(--info-color)', color: 'var(--info-color)' }}>
            <FiClock size={16} />
            <strong>This assignment is due soon.</strong> Make sure to submit before the deadline.
          </OverdueWarning>
        )}

        {/* Weekly Submissions Section */}
        {isWeeklySubmission && (
          <WeeklySubmissionsSection>
            <WeeklySubmissionsHeader>
              <WeeklySubmissionsTitle>
                <FiCalendar size={18} />
                Weekly Submissions
              </WeeklySubmissionsTitle>
            </WeeklySubmissionsHeader>
            
            {loadingWeeklySubmissions ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Loading weekly submissions...
              </div>
            ) : (
              <>
                {weeklySubmissions.length > 0 && (
                  <WeeklySubmissionsList>
                    {weeklySubmissions.map((submission) => {
                      const isSubmitted = submission.submitted_at && 
                        submission.submitted_at !== '0000-00-00 00:00:00' &&
                        submission.submitted_at !== '1970-01-01T00:00:00.000Z';
                      return (
                        <WeeklySubmissionItem
                          key={submission.id}
                          $selected={selectedWeek === submission.week_number}
                          onClick={() => {
                            setSelectedWeek(submission.week_number);
                            setCurrentSubmission(submission);
                            setContent(
                              submission.content && typeof submission.content === 'string'
                                ? { 
                                    content_json: null, 
                                    content_html: submission.content, 
                                    content_plain: toPlainTextFromHtml(submission.content) 
                                  }
                                : { 
                                    content_json: { type: 'doc', content: [] }, 
                                    content_html: '<p></p>', 
                                    content_plain: '' 
                                  }
                            );
                            if (submission.pdf_attachment) {
                              const existingPdfFile = new File([], 'existing_submission.pdf', { type: 'application/pdf' });
                              setPdfFile(existingPdfFile);
                              setPdfFileName('existing_submission.pdf');
                            } else {
                              setPdfFile(null);
                              setPdfFileName('');
                            }
                          }}
                        >
                          <WeeklySubmissionInfo>
                            <WeeklySubmissionWeek>Week {submission.week_number}</WeeklySubmissionWeek>
                            <WeeklySubmissionStatus>
                              {(() => {
                                // For submitted assignments, use backend status (correctly calculated)
                                // For drafts, check if the week is past (availability check)
                                let isLate = false;
                                if (isSubmitted && submission.submitted_at) {
                                  // Use backend status if available, otherwise calculate based on submission time
                                  if (submission.status === 'late') {
                                    isLate = true;
                                  } else if (assignment?.start_date) {
                                    // Fallback: calculate if backend status is missing
                                    isLate = isSubmissionLate(assignment.start_date, submission.week_number, submission.submitted_at, assignment.due_date);
                                  }
                                } else {
                                  // For drafts, check if week is past (availability check)
                                const weekInfo = assignment?.start_date ? isWeekAvailableForSubmission(assignment.start_date, submission.week_number) : { isLate: false };
                                  isLate = weekInfo.isLate;
                                }
                                return isSubmitted ? (
                                  <>
                                    <FiCheckCircle size={14} style={{ color: isLate ? 'var(--error-color)' : 'var(--success-color)' }} />
                                    {isLate ? 'Late - ' : ''}Submitted {formatDate(submission.submitted_at)}
                                    {submission.grade !== null && (
                                      <> • Grade: {submission.grade}/{assignment.points}</>
                                    )}
                                  </>
                                ) : (
                                  <>
                                    <FiFileText size={14} />
                                    {isLate ? 'Late Draft' : 'Draft'}
                                  </>
                                );
                              })()}
                            </WeeklySubmissionStatus>
                          </WeeklySubmissionInfo>
                        </WeeklySubmissionItem>
                      );
                    })}
                  </WeeklySubmissionsList>
                )}
                
                {availableWeeks.length === 0 && assignment?.start_date && (
                  <MaxSubmissionsMessage>
                    Weekly submissions have not started yet. The start date is {formatForDisplay(utcToChicago(assignment.start_date))}.
                  </MaxSubmissionsMessage>
                )}
                {weeklySubmissions.length >= maxWeeklySubmissions && (
                  <MaxSubmissionsMessage>
                    You have reached the maximum of {maxWeeklySubmissions} weekly submissions.
                  </MaxSubmissionsMessage>
                )}
              </>
            )}
          </WeeklySubmissionsSection>
        )}

        {/* Submission Section */}
        <SubmissionSection>
          {/* Error/Success Messages */}
          {error && (
            <ErrorMessage
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FiAlertCircle size={16} />
              {error}
            </ErrorMessage>
          )}

          {success && (
            <SuccessMessage
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <FiCheckCircle size={16} />
              {success}
            </SuccessMessage>
          )}

          {/* Your Submission Card */}
          <SubmissionCard>
            <CardBody>
              <form onSubmit={handleSubmit}>
                {/* Text Response */}
                <FormGroup>
                  <FieldLabel>Your Response *</FieldLabel>
                  {/* Week Selector - Only for weekly assignments */}
                  {isWeeklySubmission && (
                <WeekSelectorSection>
                  <WeekSelectorLabel>Select Week:</WeekSelectorLabel>
                  <WeekSelect
                    value={selectedWeek || ''}
                    onChange={(e) => {
                      const weekNum = e.target.value ? parseInt(e.target.value) : null;
                      setSelectedWeek(weekNum);
                      if (weekNum) {
                        const submission = weeklySubmissions.find(s => s.week_number === weekNum);
                        if (submission) {
                          setCurrentSubmission(submission);
                          setContent(
                            submission.content && typeof submission.content === 'string'
                              ? { 
                                  content_json: null, 
                                  content_html: submission.content, 
                                  content_plain: toPlainTextFromHtml(submission.content) 
                                }
                              : { 
                                  content_json: { type: 'doc', content: [] }, 
                                  content_html: '<p></p>', 
                                  content_plain: '' 
                                }
                          );
                          if (submission.pdf_attachment) {
                            const existingPdfFile = new File([], 'existing_submission.pdf', { type: 'application/pdf' });
                            setPdfFile(existingPdfFile);
                            setPdfFileName('existing_submission.pdf');
                          } else {
                            setPdfFile(null);
                            setPdfFileName('');
                          }
                        } else {
                          // New week - clear content
                          setCurrentSubmission(null);
                          setContent({ 
                            content_json: { type: 'doc', content: [] }, 
                            content_html: '<p></p>', 
                            content_plain: '' 
                          });
                          setPdfFile(null);
                          setPdfFileName('');
                        }
                      } else {
                        setCurrentSubmission(null);
                        setContent({ 
                          content_json: { type: 'doc', content: [] }, 
                          content_html: '<p></p>', 
                          content_plain: '' 
                        });
                        setPdfFile(null);
                        setPdfFileName('');
                      }
                    }}
                  >
                    <option value="">Choose Week</option>
                    {availableWeeks.map(weekNum => {
                      const submission = weeklySubmissions.find(s => s.week_number === weekNum);
                      // For submitted assignments, use backend status; for unsubmitted, check if week is past
                      const weekInfo = isWeekAvailableForSubmission(assignment.start_date, weekNum);
                      const isLate = submission?.submitted_at
                        ? (submission.status === 'late' || (submission.submitted_at && isSubmissionLate(assignment.start_date, weekNum, submission.submitted_at, assignment.due_date)))
                        : weekInfo.isLate;
                      const statusText = submission 
                        ? (submission.submitted_at ? ' (Submitted)' : ' (Draft)')
                        : '';
                      const lateText = isLate && !submission ? ' (Late)' : '';
                      return (
                        <option key={weekNum} value={weekNum}>
                          Week {weekNum}{statusText}{lateText}
                        </option>
                      );
                    })}
                  </WeekSelect>
                </WeekSelectorSection>
                  )}
                  {isWeeklySubmission && availableWeeks.length > 0 && weeklySubmissions.length < maxWeeklySubmissions && (() => {
                  // Count only submitted weeks (not drafts)
                  const submittedWeeks = weeklySubmissions.filter(sub => {
                    const submittedAt = sub.submitted_at;
                    return submittedAt && 
                      submittedAt !== '' &&
                      submittedAt !== '0000-00-00 00:00:00' &&
                      submittedAt !== '1970-01-01T00:00:00.000Z' &&
                      submittedAt !== '0000-00-00T00:00:00.000Z';
                  }).map(sub => sub.week_number);
                  
                  // Check if all available weeks have been submitted (not just drafted)
                  const allAvailableWeeksSubmitted = availableWeeks.every(weekNum => submittedWeeks.includes(weekNum));
                  
                  return allAvailableWeeksSubmitted && (
                      <MaxSubmissionsMessage style={{ marginTop: '0.75rem', marginBottom: '0.75rem' }}>
                      All available weeks have been submitted. Week {availableWeeks[availableWeeks.length - 1] + 1} will be available after the current week ends.
                    </MaxSubmissionsMessage>
                  );
                })()}
                  <div className={styles.tiptapEditor}>
                    <RichTextEditorTipTap
                      value={content}
                      onChange={(newContent) => {
                        setContent(newContent);
                      }}
                      placeholder="Enter your response here..."
                      disabled={isReadOnly}
                    />
                  </div>
                  <CharacterCount>
                    {(typeof content === 'string' ? content : content?.html || '').replace(/<[^>]*>/g, '').length} characters
                  </CharacterCount>
                  
                  {/* Auto-save status indicator - only show when not in read-only mode */}
                  {!isReadOnly && (autoSaveStatus !== 'idle' || isDirty || lastSaved) && (
                    <AutoSaveIndicator className={autoSaveStatus === 'idle' && isDirty ? 'unsaved' : autoSaveStatus}>
                      {autoSaveStatus === 'saving' && (
                        <>
                          <SmallSpinner />
                          Saving...
                        </>
                      )}
                      {autoSaveStatus === 'saved' && (
                        <>
                          <FiCheck size={12} />
                          Saved {lastSaved?.toLocaleTimeString()}
                        </>
                      )}
                      {autoSaveStatus === 'error' && (
                        <>
                          <FiAlertCircle size={12} />
                          {autoSaveError || 'Save failed'}
                          <RetryButton onClick={retryAutoSave}>
                            <FiRefreshCw size={10} style={{ marginRight: '4px' }} />
                            Retry
                          </RetryButton>
                        </>
                      )}
                      {autoSaveStatus === 'offline' && (
                        <>
                          <FiWifiOff size={12} />
                          Offline - changes will save when reconnected
                        </>
                      )}
                      {autoSaveStatus === 'idle' && isDirty && (
                        <>
                          <FiClock size={12} />
                          Unsaved changes
                        </>
                      )}
                      {autoSaveStatus === 'idle' && !isDirty && lastSaved && (
                        <>
                          <FiCheck size={12} />
                          Last saved {lastSaved.toLocaleTimeString()}
                        </>
                      )}
                    </AutoSaveIndicator>
                  )}
                </FormGroup>

                {/* PDF Upload */}
                {canEdit && (
                  <FormGroup>
                    <FieldLabel>PDF attachment (Optional) · Max 10MB</FieldLabel>
                    <PdfUploadRow>
                      <FileInput
                        type="file"
                        accept="application/pdf"
                        onChange={handlePdfFileChange}
                        disabled={loading}
                        id="pdf-upload"
                        aria-label="Upload PDF file"
                      />
                      <PdfUploadButton
                        type="button"
                        onClick={() => document.getElementById('pdf-upload').click()}
                        disabled={loading}
                      >
                        <FiFileText />
                        {pdfFileName ? 'Replace File' : 'Upload File'}
                      </PdfUploadButton>
                      {pdfFileName && (
                        <>
                          <PdfSelectedText>
                            Selected: {truncateFileName(pdfFileName)}
                          </PdfSelectedText>
                          <PdfRemoveButton
                            type="button"
                            onClick={removePdfFile}
                            aria-label="Remove PDF file"
                          >
                            Remove
                          </PdfRemoveButton>
                        </>
                      )}
                    </PdfUploadRow>
                    {pdfError && (
                      <PdfErrorText>
                        {pdfError}
                      </PdfErrorText>
                    )}
                  </FormGroup>
                )}

                {/* Buttons */}
                {canEdit && (
                  <CardFooter>
                    <Button 
                      type="button" 
                      className="secondary" 
                      onClick={saveDraft}
                      disabled={isReadOnly || loading || saving || (() => {
                        const textContent = typeof content === 'object' ? (content.content_plain || content.text || '') : content;
                        const hasText = textContent.replace(/<[^>]*>/g, '').trim().length > 0;
                        const hasPdf = !!pdfFile;
                        return !hasText && !hasPdf;
                      })()}
                    >
                      {saving ? (
                        <>
                          <LoadingSpinner />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave size={16} />
                          Save Draft
                        </>
                      )}
                    </Button>
                    <Button 
                      type="submit" 
                      className="primary" 
                      disabled={loading || assignment?.course_is_active === false || !canSubmit || (() => {
                        const textContent = typeof content === 'object' ? (content.content_plain || content.text || '') : content;
                        const hasText = textContent.replace(/<[^>]*>/g, '').trim().length > 0;
                        const hasPdf = !!pdfFile;
                        return !hasText && !hasPdf;
                      })()}
                      title={
                        !canSubmit 
                          ? 'Please select a week for weekly submissions' 
                          : assignment?.course_is_active === false 
                            ? 'Course is inactive - submissions are disabled' 
                            : undefined
                      }
                    >
                      {loading ? (
                        <>
                          <LoadingSpinner />
                          {currentSubmission && currentSubmission.submitted_at ? 'Resubmitting...' : 'Submitting...'}
                        </>
                      ) : (
                        <>
                          <FiSend size={16} />
                          {currentSubmission && currentSubmission.submitted_at 
                            ? (isWeeklySubmission && selectedWeek ? `Resubmit Week ${selectedWeek}` : 'Resubmit Assignment')
                            : (isWeeklySubmission && selectedWeek ? `Submit Week ${selectedWeek}` : 'Submit Assignment')}
                        </>
                      )}
                    </Button>
                  </CardFooter>
                )}
              </form>
            </CardBody>
          </SubmissionCard>

          {/* Submitted PDF Attachment Display */}
          {currentSubmission && currentSubmission.pdf_attachment && hasRealSubmission && (
            <SubmissionCard>
              <CardBody>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                  <FiFileText size={18} color="var(--primary-color)" />
                  <FieldLabel style={{ marginBottom: 0 }}>Submitted PDF Attachment</FieldLabel>
                </div>
                <div style={{ marginTop: '0.75rem' }}>
                  <PdfAttachmentChip onClick={handleViewSubmittedPdf}>
                    <FiEye size={14} />
                    View PDF Attachment
                  </PdfAttachmentChip>
                </div>
              </CardBody>
            </SubmissionCard>
          )}

          {/* Grading & Feedback Card */}
          {currentSubmission && currentSubmission.grade !== null && (
            <FeedbackCard>
              <FeedbackHeader>
                <FiMessageSquare size={18} />
                {isWeeklySubmission ? `Week ${currentSubmission.week_number} - Grading & Feedback` : 'Grading & Feedback'}
              </FeedbackHeader>
              <FeedbackContent>
                <GradeDisplay>
                  <GradeValue>{currentSubmission.grade}</GradeValue>
                  <GradeMax>/ {assignment.points}</GradeMax>
                </GradeDisplay>
                {currentSubmission.feedback && (
                  <>
                    <div style={{ fontWeight: 600, marginBottom: '8px', fontSize: '0.875rem' }}>
                      Instructor Feedback:
                    </div>
                    <FeedbackText>{currentSubmission.feedback}</FeedbackText>
                  </>
                )}
                {currentSubmission.graded_at && (
                  <FeedbackTimestamp>
                    Graded on {formatDate(currentSubmission.graded_at)}
                  </FeedbackTimestamp>
                )}
              </FeedbackContent>
            </FeedbackCard>
          )}
        </SubmissionSection>

        {/* PDF Viewer */}
        <PdfViewer
          isOpen={showPdfViewer}
          onClose={() => {
            setShowPdfViewer(false);
            setShowInstructorPdf(false);
            setCurrentPdfData(null);
          }}
          pdfBase64={currentPdfData}
          fileName={getCurrentPdfFileName()}
        />
      </Container>
    </PageContainer>
  );
}

export default AssignmentSubmissionInline;
